import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import apiClient from '@/config/api';
import { EVENTS, track } from '@/lib/analytics';

/** RFC 5987 `filename*=UTF-8''…` first (keeps Devanagari titles), then plain filename. */
export function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch (e) { /* fall through */ }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : fallback;
}

/**
 * Downloads a PDF export. Every plan can export — Free copies carry the
 * "Made with FrameFlow" footer, so this never shows a locked door, only a
 * quiet note about the watermark after the file is already in hand.
 *
 *   const { exportPdf, exporting } = usePdfExport();
 *   exportPdf(`/films/${filmId}/export/shot-list.pdf`, { kind: 'shot_list', fallbackName: 'Shot list.pdf' });
 */
export function usePdfExport() {
  const [exporting, setExporting] = useState(false);

  const exportPdf = useCallback(async (path, { kind, fallbackName = 'FrameFlow export.pdf' } = {}) => {
    if (exporting) return;
    setExporting(true);
    track(EVENTS.PDF_EXPORT_CLICKED, { kind });
    try {
      const res = await apiClient.get(path, { responseType: 'blob' });
      const name = filenameFromDisposition(res.headers?.['content-disposition'], fallbackName);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      if (res.headers?.['x-frameflow-watermarked'] === 'true') {
        toast.success('PDF downloaded', { description: 'Free exports carry a small FrameFlow footer. Upgrade for a clean copy.' });
      } else {
        toast.success('PDF downloaded');
      }
    } catch (err) {
      const status = err?.response?.status;
      track(EVENTS.PDF_EXPORT_FAILED, { kind, status });
      const msg = status === 429
        ? 'You have exported a lot in the last hour. Please try again in a bit.'
        : status === 403
          ? "You don't have access to export this film."
          : 'Could not create the PDF. Please try again.';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  return { exportPdf, exporting };
}
